import { Wallet } from "lucide-react";
import { ManualeLayout, Scheda, P, Steps, Bullets, Callout, FAQ } from "@/pages/manuali/ManualeLayout";

const A = "#0d9488";
const SEZIONI = [
  { id: "intro", titolo: "Cos'è la Prima Nota" },
  { id: "movimenti", titolo: "Movimenti e ordinamento" },
  { id: "versamenti", titolo: "Consegna e versamento" },
  { id: "sospesi", titolo: "Sospesi e cruscotto" },
  { id: "chiusura", titolo: "Chiudere i sospesi" },
  { id: "report", titolo: "Distinta e report PDF" },
  { id: "riconciliazione", titolo: "Riconciliazione Compagnie" },
  { id: "impostazioni", titolo: "Impostazioni e autorizzazioni" },
  { id: "faq", titolo: "FAQ e avvertenze" },
];

export default function ManualePrimaNota() {
  return (
    <ManualeLayout titolo="Prima Nota Cassa" sottotitolo="Registro unico della cassa dell'Agenzia" icona={Wallet} accent={A} sezioni={SEZIONI}>
      <Scheda id="intro" n="1" titolo="Cos'è la Prima Nota" accent={A}>
        <P>La <b>Prima Nota Cassa</b> (menu <b>Prima Nota</b>, indirizzo <b>/prima-nota</b>) è il registro unico della cassa dell'Agenzia: raccoglie tutti i movimenti di entrata/uscita, i versamenti in banca, i sospesi da recuperare e la riconciliazione con i fogli cassa delle compagnie.</P>
        <Callout tipo="info">Molti movimenti arrivano automaticamente: quando in <b>Contabilità di Agenzia</b> si incassa un titolo, l'incasso confluisce qui (catena unica), senza doppie registrazioni.</Callout>
        <P>Ha due viste: <b>Movimenti</b> e <b>Riconciliazione Compagnie</b>.</P>
      </Scheda>

      <Scheda id="movimenti" n="2" titolo="Movimenti e ordinamento" accent={A}>
        <Steps items={[
          "Filtra per periodo (Dal/Al), Causale, Sede o cerca cliente/polizza.",
          "Usa 'Ordina per' (Data / Cliente / Intermediario) oppure clicca le intestazioni di colonna.",
          "Con 'Nuovo movimento' aggiungi una riga manuale (data, causale, cliente, entrata/uscita, modalità...).",
        ]} />
        <Bullets items={[
          "Il riepilogo in alto mostra Entrate, Uscite, Saldo, Provvigioni e Sospesi aperti.",
          "Con Excel/PDF esporti l'elenco filtrato.",
        ]} />
      </Scheda>

      <Scheda id="versamenti" n="3" titolo="Consegna e versamento" accent={A}>
        <P>Sulle righe da versare trovi due azioni con <b>calendario</b> per indicare la <b>data effettiva</b>:</P>
        <Bullets items={[
          "Consegnato in agenzia: segna che i contanti/assegni sono stati consegnati (scegli la data reale, non solo oggi).",
          "Versato in banca: segna l'avvenuto versamento (con la sua data).",
          "Il filtro 'Solo da versare' mostra ciò che resta da portare in banca.",
        ]} />
      </Scheda>

      <Scheda id="sospesi" n="4" titolo="Sospesi e cruscotto" accent={A}>
        <P>Ogni movimento può avere uno <b>stato operativo</b> quando resta aperto:</P>
        <Bullets items={[
          "Sospeso (generico), Sospeso parziale (con importo residuo), Sospeso Cofidis/Compass, In transito (subagente), Bonifico da verificare.",
          "Il cruscotto rosso in alto mostra gli importi aperti per tipo (clicca un badge per filtrare).",
          "I sospesi aperti da più giorni della soglia impostata sono evidenziati in rosso con badge 'N gg'.",
          "Il filtro 'Solo sospesi' isola tutte le posizioni aperte.",
        ]} />
      </Scheda>

      <Scheda id="chiusura" n="5" titolo="Chiudere i sospesi" accent={A}>
        <P>La chiusura di un sospeso è tracciata (chi e quando) ed è riservata alla <b>Direzione e ai colleghi autorizzati</b> (es. Antonio, Melania).</P>
        <Bullets items={[
          "Il pulsante verde 'Chiudi sospeso' compare solo agli autorizzati e chiede il motivo.",
          "Sospeso Cofidis/Compass: si chiude automaticamente quando registri il bonifico ricevuto (Versato in banca).",
          "Lo 'Storico chiusure' elenca chi ha chiuso cosa e quando, filtrabile per operatore e periodo.",
        ]} />
      </Scheda>

      <Scheda id="report" n="6" titolo="Distinta e report PDF" accent={A}>
        <Bullets items={[
          "Distinta versamento: PDF che separa Contanti e Assegni (POS/bonifici esclusi perché già in banca), con righe firma.",
          "Report giornaliero: riepilogo di fine giornata per modalità e produttore, da stampare per l'ufficio.",
          "Report sospesi: elenco dei sospesi aperti per tipo, per la riunione settimanale.",
        ]} />
      </Scheda>

      <Scheda id="riconciliazione" n="7" titolo="Riconciliazione Compagnie" accent={A}>
        <P>Nella sotto-tab <b>Riconciliazione Compagnie</b> confronti i Fogli Cassa delle compagnie con la Prima Nota.</P>
        <Steps items={[
          "Scegli la compagnia e carica il file (Excel Generali; PDF Assimoco, Groupama, Essellepi/SLP).",
          "Premi Anteprima per controllare le righe rilevate, poi Importa.",
          "Premi Riconcilia: il periodo è precompilato con le date del foglio (modificabile).",
        ]} />
        <Bullets items={[
          "MATCH = presente nel foglio compagnia e incassato in Prima Nota.",
          "ERRORE = incassato in Prima Nota ma assente nel foglio (confrontato solo nel periodo indicato).",
          "ALLARME = presente nel foglio compagnia ma assente in Prima Nota; con 'Crea sospesi automatici' genera i relativi sospesi.",
          "L'esito si salva: usa 'Riapri' per riconsultarlo e 'Esporta PDF' per l'archivio o il confronto con la compagnia.",
        ]} />
      </Scheda>

      <Scheda id="impostazioni" n="8" titolo="Impostazioni e autorizzazioni" accent={A}>
        <P>Dal pulsante <b>Impostazioni sospesi</b> (solo Direzione) gestisci:</P>
        <Bullets items={[
          "Gli operatori autorizzati a chiudere i sospesi (aggiungi/rimuovi email, es. Melania).",
          "La soglia in giorni oltre la quale un sospeso è considerato 'in ritardo' (evidenza rossa).",
        ]} />
      </Scheda>

      <Scheda id="faq" n="9" titolo="FAQ e avvertenze" accent={A}>
        <FAQ items={[
          { q: "Perché un incasso compare qui senza averlo inserito?", a: "Perché è arrivato dalla Contabilità: incassando un titolo si crea il movimento nel Foglio Cassa del produttore e confluisce in Prima Nota (catena unica)." },
          { q: "Perché nella riconciliazione ho tanti ERRORE?", a: "Confronta un periodo alla volta: il periodo è precompilato con le date del foglio caricato. Un foglio parziale confrontato con tutto lo storico produce falsi errori." },
          { q: "Chi può chiudere i sospesi?", a: "La Direzione e i colleghi autorizzati in 'Impostazioni sospesi'. Ogni chiusura viene registrata nello Storico chiusure." },
          { q: "Come salvo il manuale in PDF?", a: "Usa il pulsante Stampa/Salva PDF in alto in questa pagina e scegli 'Salva come PDF'." },
        ]} />
      </Scheda>
    </ManualeLayout>
  );
}
